/**
 * Cart add-on fee reconciler.
 *
 * Ensures that fee line items in the cart match the expected state derived from
 * `_addon_rule_payload` properties on non-fee lines.
 *
 * The reconciliation algorithm:
 *  1. GET /cart.js to read current cart state
 *  2. Build expected fee keys from non-fee lines' _addon_rule_payload
 *     — When the same key has multiple entries (e.g. per_order with different
 *       variants), the most expensive variant wins (highest fee_price).
 *  3. Index existing fee lines by _addon_key
 *  4. Remove stale fee lines (keys no longer expected)
 *  5. Remove duplicate fee lines for the same key
 *  6. Evict fee lines whose variant doesn't match the expected (most expensive) variant
 *  7. Enforce fee quantity = 1 for all remaining fee lines
 *  8. Add missing + evicted fee lines (qty 1) with fee markers
 *  9. Return updated sections so cart UI re-renders
 *
 * Event-driven scheduling:
 * - `CartAddEvent` and any `cart:update` whose new line count differs from the
 *   last-known count are treated as structural and reconcile IMMEDIATELY, so
 *   stale fees are removed before the customer can reach checkout.
 * - Remaining events (quantity-only changes, e.g. rapid stepper clicks) are
 *   debounced via `QUANTITY_DEBOUNCE_MS` to coalesce bursts.
 */

import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {{ _is_addon?: string, _addon_type?: string, _addon_key?: string, _addon_rule_payload?: string }} AddonProperties
 * @typedef {{ key: string, variant_id: number | string, quantity: number, properties?: AddonProperties }} CartItem
 * @typedef {{ items?: CartItem[] }} CartResponse
 * @typedef {{ rule_code: string, fee_variant_id: number | string, fee_price?: number | string, scope: string, addon_key: string, expected_quantity?: number }} AddonFeeEntry
 */

/** @type {boolean} */
let isReconciling = false;

/** @type {AbortController|null} */
let pendingController = null;

/**
 * Number of non-fee line items observed on the last reconcile. Used by the
 * event classifier to tell structural changes (add / remove) apart from
 * quantity-only changes.
 *
 * `null` means we haven't observed the cart yet — in that case the classifier
 * fails safe to "structural".
 *
 * @type {number | null}
 */
let lastKnownNonFeeLineCount = null;

/**
 * Runs the full reconciliation cycle.
 * Idempotent — safe to call multiple times; uses a mutex to serialise.
 *
 * @returns {Promise<void>}
 */
export async function reconcileAddonFees() {
  // If already reconciling, abort the previous run and start fresh
  if (isReconciling && pendingController) {
    pendingController.abort();
  }

  isReconciling = true;
  pendingController = new AbortController();
  const { signal } = pendingController;

  try {
    // 1. Fetch current cart
    const cart = await fetchCart(signal);

    if (!cart || !cart.items) {
      return;
    }

    // Record a structural fingerprint so future cart:update events can be
    // classified as structural (add/remove) vs quantity-only.
    lastKnownNonFeeLineCount = countNonFeeItems(cart.items);

    // 2. Build expected fee map  { addon_key → fee descriptor }
    const expectedFees = buildExpectedFees(cart.items);

    // 3. Index existing fee lines  { addon_key → { item, duplicates[] } }
    const existingFees = indexExistingFees(cart.items);

    // 4-6. Compute updates (removes + quantity fixes + variant evictions)
    const { updates, evictedKeys } = computeUpdates(expectedFees, existingFees);

    // 7. Compute additions (missing keys + evicted keys that need the correct variant)
    const additions = computeAdditions(expectedFees, existingFees, evictedKeys);

    const hasChanges = Object.keys(updates).length > 0 || additions.length > 0;
    if (!hasChanges) {
      return;
    }

    // Collect section IDs for re-rendering
    const sectionIds = getCartSectionIds();

    // Execute updates first (remove stale / fix qty), then additions
    let finalSections = null;

    if (Object.keys(updates).length > 0) {
      finalSections = await executeUpdates(updates, sectionIds, signal);
    }

    if (additions.length > 0) {
      finalSections = await executeAdditions(additions, sectionIds, signal);
    }

    // Dispatch cart update event for UI re-render
    dispatchCartUpdate(sectionIds, finalSections);
  } catch (error) {
    if (error.name === 'AbortError') return; // Intentional cancel
    console.error('[addon-reconciler] Reconciliation failed:', error);
  } finally {
    isReconciling = false;
    pendingController = null;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * @param {AbortSignal} signal
 * @returns {Promise<CartResponse|null>}
 */
async function fetchCart(signal) {
  const response = await fetch(`${Theme.routes.root}cart.js`, { signal });
  if (!response.ok) return null;
  return response.json();
}

/**
 * Scans non-fee lines for `_addon_rule_payload` and builds a map of expected fees.
 *
 * When the same `addon_key` appears across multiple line items with different
 * `fee_variant_id` values (e.g. two products both request a per_order addon
 * but chose different variants), the entry with the highest `fee_price` wins.
 *
 * @param {CartItem[]} items - cart.items
 * @returns {Map<string, AddonFeeEntry>}
 */
function buildExpectedFees(items) {
  /** @type {Map<string, AddonFeeEntry>} */
  const expected = new Map();

  for (const item of items) {
    // Skip fee lines
    if (item.properties?._is_addon === 'true') continue;

    const rawPayload = item.properties?._addon_rule_payload;
    if (!rawPayload) continue;

    let entries;
    try {
      entries = JSON.parse(rawPayload);
    } catch {
      continue;
    }

    if (!Array.isArray(entries)) continue;

    for (const entry of entries) {
      if (!entry.addon_key || !entry.fee_variant_id) continue;

      const existing = expected.get(entry.addon_key);

      const expectedQuantity =
        entry.scope === 'per_product'
          ? (existing?.expected_quantity ?? 0) + item.quantity
          : 1;

      if (!existing || Number(entry.fee_price || 0) > Number(existing.fee_price || 0)) {
        expected.set(entry.addon_key, {
          ...entry,
          expected_quantity: expectedQuantity,
        });
      } else {
        existing.expected_quantity = expectedQuantity;
      }
    }
  }

  return expected;
}

/**
 * Indexes existing fee lines in the cart by their `_addon_key`.
 *
 * @param {CartItem[]} items - cart.items
 * @returns {Map<string, {item: CartItem, duplicates: CartItem[]}>}
 */
function indexExistingFees(items) {
  /** @type {Map<string, {item: CartItem, duplicates: CartItem[]}>} */
  const index = new Map();

  for (const item of items) {
    if (item.properties?._is_addon !== 'true') continue;
    if (item.properties?._addon_type !== 'fee') continue;

    const key = item.properties?._addon_key;
    if (!key) continue;

    const existing = index.get(key);
    if (existing) {
      existing.duplicates.push(item);
    } else {
      index.set(key, { item, duplicates: [] });
    }
  }

  return index;
}

/**
 * Returns an updates object for /cart/update.js:
 * - Stale fee lines → quantity 0
 * - Duplicate fee lines → quantity 0
 * - Fee lines with wrong variant (evicted) → quantity 0
 * - Fee lines with wrong quantity → quantity 1
 *
 * Also returns a set of evicted keys — addon_keys whose existing variant
 * didn't match the expected (most expensive) variant and were removed.
 * `computeAdditions` uses this to re-add the correct variant.
 *
 * @param {Map<string, AddonFeeEntry>} expectedFees
 * @param {Map<string, {item: CartItem, duplicates: CartItem[]}>} existingFees
 * @returns {{ updates: Record<string, number>, evictedKeys: Set<string> }}
 */
function computeUpdates(expectedFees, existingFees) {
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Set<string>} */
  const evictedKeys = new Set();

  for (const [key, data] of existingFees) {
    if (!expectedFees.has(key)) {
      // Stale — remove primary + all duplicates
      updates[data.item.key] = 0;
      for (const dup of data.duplicates) {
        updates[dup.key] = 0;
      }
    } else {
      const expected = /** @type {AddonFeeEntry} */ (expectedFees.get(key));

      // Check if the existing fee variant matches the expected (most expensive) variant
      const existingVariantId = String(data.item.variant_id);
      const expectedVariantId = String(expected.fee_variant_id);

      if (existingVariantId !== expectedVariantId) {
        // Wrong variant — evict the existing fee so the correct one gets added
        updates[data.item.key] = 0;
        evictedKeys.add(key);
      } else if (data.item.quantity !== (expected.expected_quantity ?? 1)) {
        // Correct variant but wrong qty — enforce expected quantity
        updates[data.item.key] = expected.expected_quantity ?? 1;
      }

      // Remove all duplicates regardless
      for (const dup of data.duplicates) {
        updates[dup.key] = 0;
      }
    }
  }

  return { updates, evictedKeys };
}

/**
 * Returns items to add via /cart/add.js — fee lines that are expected but
 * either missing entirely or were evicted (wrong variant removed).
 *
 * @param {Map<string, AddonFeeEntry>} expectedFees
 * @param {Map<string, {item: CartItem, duplicates: CartItem[]}>} existingFees
 * @param {Set<string>} evictedKeys - Keys whose existing variant was removed by computeUpdates
 * @returns {Array<Object>}
 */
function computeAdditions(expectedFees, existingFees, evictedKeys) {
  const additions = [];

  for (const [key, entry] of expectedFees) {
    // Add if the key is completely missing OR was evicted (wrong variant removed)
    if (!existingFees.has(key) || evictedKeys.has(key)) {
      additions.push({
        id: entry.fee_variant_id,
        quantity: entry.expected_quantity ?? 1,
        properties: {
          _is_addon: 'true',
          _addon_type: 'fee',
          _addon_rule: entry.rule_code,
          _addon_scope: entry.scope,
          _addon_key: entry.addon_key,
        },
      });
    }
  }

  return additions;
}

/**
 * Executes /cart/update.js with the updates map.
 *
 * @param {Record<string, number>} updates
 * @param {string[]} sectionIds
 * @param {AbortSignal} signal
 */
async function executeUpdates(updates, sectionIds, signal) {
  const body = JSON.stringify({
    updates,
    sections: sectionIds.join(','),
  });

  const response = await fetch(`${Theme.routes.cart_update_url}`, {
    ...fetchConfig('json', { body }),
    keepalive: true,
    signal,
  });

  if (!response.ok) {
    throw new Error(`[addon-reconciler] /cart/update failed: ${response.status}`);
  }

  const data = await response.json();
  return data.sections || null;
}

/**
 * Executes /cart/add.js with the items array. Returns sections from the response.
 *
 * @param {Array<Object>} items
 * @param {string[]} sectionIds
 * @param {AbortSignal} signal
 * @returns {Promise<Record<string, string>|null>}
 */
async function executeAdditions(items, sectionIds, signal) {
  const body = JSON.stringify({
    items,
    sections: sectionIds.join(','),
  });

  const response = await fetch(`${Theme.routes.cart_add_url}`, {
    ...fetchConfig('json', { body }),
    keepalive: true,
    signal,
  });

  if (!response.ok) {
    throw new Error(`[addon-reconciler] /cart/add failed: ${response.status}`);
  }

  const data = await response.json();
  return data.sections || null;
}

/**
 * Collects section IDs from all cart-items-component elements in the DOM.
 *
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

/**
 * Dispatches a CartUpdateEvent so the cart UI (drawer, page) re-renders.
 * Falls back to section-renderer if no sections HTML is available.
 *
 * @param {string[]} sectionIds
 * @param {Record<string, string>|null} sections
 */
function dispatchCartUpdate(sectionIds, sections) {
  if (sections) {
    // Morph each section and dispatch event
    for (const sectionId of sectionIds) {
      if (sections[sectionId]) {
        morphSection(sectionId, sections[sectionId]);
      }
    }
    
    document.dispatchEvent(
      new CartUpdateEvent({}, 'addon-reconciler', {
        source: 'addon-reconciler',
        sections,
      })
    );
  } else {
    // Fallback: re-fetch sections
    for (const sectionId of sectionIds) {
      sectionRenderer.renderSection(sectionId, { cache: false });
    }
  }
}

// ─── Event classification ──────────────────────────────────────────────────

/**
 * Counts non-fee items in a cart.js response.
 *
 * @param {CartItem[]} items
 * @returns {number}
 */
function countNonFeeItems(items) {
  let count = 0;
  for (const item of items) {
    if (item.properties?._is_addon !== 'true') count++;
  }
  return count;
}

/**
 * Counts non-fee cart line rows in a sections payload returned by
 * `/cart/change.js` or `/cart/add.js`. Walks each section HTML string,
 * parses it, and counts real cart line rows that are NOT marked as addon fee
 * rows. Header rows can share cart row classes, so only rows with
 * `data-cart-line` are considered line items.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null} line count, or `null` if no section contained
 *   cart-item rows (e.g. the event came from a non-cart context).
 */
function countNonFeeRowsInSections(sections) {
  if (!sections || typeof sections !== 'object') return null;

  for (const key of Object.keys(sections)) {
    const html = sections[key];
    if (typeof html !== 'string' || html.length === 0) continue;

    let doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch {
      continue;
    }

    // Only trust sections that actually contain cart line item rows.
    const anyLineRow = doc.querySelector('[data-cart-line]');
    if (!anyLineRow) continue;

    const nonFeeRows = doc.querySelectorAll(
      '.cart-items__table-row[data-cart-line]:not(.cart-items__table-row--addon-fee)'
    );
    return nonFeeRows.length;
  }

  return null;
}

/**
 * Classifies a cart:update event as either structural (a non-fee line item
 * was added or removed) or quantity-only (line count unchanged).
 *
 * Structural events must be reconciled immediately so stale fee lines are
 * removed before the customer can checkout. Quantity-only events can safely
 * be debounced to coalesce rapid stepper ticks.
 *
 * Fails safe to 'structural' when classification isn't possible (no sections
 * payload, unparseable HTML, first-ever event) — better to reconcile too
 * often than to delay a structural change behind the debounce window.
 *
 * @param {Event} event
 * @returns {'structural' | 'quantity'}
 */
function classifyCartEvent(event) {
  // Additions are always structural — a new line item has appeared.
  if (event instanceof CartAddEvent) return 'structural';

  const sections = /** @type {any} */ (event).detail?.data?.sections;
  const newCount = countNonFeeRowsInSections(sections);

  if (newCount === null || lastKnownNonFeeLineCount === null) {
    return 'structural';
  }

  return newCount !== lastKnownNonFeeLineCount ? 'structural' : 'quantity';
}

// ─── Auto-reconcile on cart mutations ──────────────────────────────────────

/** Debounce window for quantity-only events (rapid stepper clicks). */
const QUANTITY_DEBOUNCE_MS = 350;

/** @type {ReturnType<typeof setTimeout> | null} */
let debounceTimer = null;

/**
 * Schedules a reconcile. Structural changes bypass the debounce so that
 * adds and removes are reflected in the cart before the customer can reach
 * checkout. Quantity-only changes stay debounced so that rapid stepper
 * clicks are coalesced into a single run.
 *
 * @param {{ immediate: boolean }} options
 */
function scheduleReconcile({ immediate }) {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  if (immediate) {
    reconcileAddonFees();
    return;
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    reconcileAddonFees();
  }, QUANTITY_DEBOUNCE_MS);
}

/**
 * Listens for cart:update events and triggers reconciliation,
 * skipping events that originated from the reconciler itself.
 */
function initAutoReconcile() {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    // Skip our own events to avoid infinite loops
    if (/** @type {any} */ (event).detail?.data?.source === 'addon-reconciler') return;

    const classification = classifyCartEvent(event);
    scheduleReconcile({ immediate: classification === 'structural' });
  });
}

initAutoReconcile();
